import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import React from 'react';

import { StructureCapabilitiesPanelProps } from './StructureCapabilitiesPanel.props';
import { ColorProps } from '../../../@types';
import { Checkbox, CheckboxColorProps } from '../../../components/atoms/Checkbox';
import { FormControlLabel } from '../../../components/atoms/FormControlLabel';
import { Panel } from '../../../components/atoms/Panel';
import { Switch } from '../../../components/atoms/Switch';
import { STRUCTURE_ATTRIBUTE_TYPE_FIELDS } from '../../../constants/structure';
import {
  CENTRAL_SUPPLY_WAREHOUSE,
  CENTRAL_SUPPLY_WAREHOUSE_HELPER_TEXT,
  GLOBAL_RETURNS_LOCATION,
  StructureTypeFields,
} from '../../../constants/structureType';

// Address, contact, trading hours, reference numbers and site schema each get
// their own panel further down the page, so listing them here as well would say
// the same thing twice.
const LISTED_CAPABILITIES = StructureTypeFields.filter(
  (field) => !STRUCTURE_ATTRIBUTE_TYPE_FIELDS.includes(field.key)
);

/**
 * What this structure's type allows, and the two markers an operator can set on
 * top of it.
 *
 * The capability list is read-only — it is a property of the structure type, not
 * of this structure — but it explains why the rest of the page has the shape it
 * has, which is why it sits at the top rather than buried at the bottom.
 */
export const StructureCapabilitiesPanel: React.FC<StructureCapabilitiesPanelProps> = ({
  propertyTypes,
  showCapabilityList = true,
  isActive,
  canEditActive,
  onActiveChange,
  showGlobalReturns,
  globalReturnsChecked,
  canEditGlobalReturns,
  onGlobalReturnsChange,
  showCentralSupplyWarehouse = false,
  centralSupplyWarehouseChecked = false,
  onCentralSupplyWarehouseChange,
}) => (
  <Panel title={showCapabilityList ? 'Status & capabilities' : 'Status'}>
    <FormControlLabel
      control={
        <Switch
          checked={isActive}
          onChange={onActiveChange}
          disabled={!canEditActive}
          color={ColorProps.Primary}
        />
      }
      label="Active"
    />

    {showCapabilityList && (
      <Box sx={{ borderTop: 1, borderColor: 'v4.border', mt: 1, pt: 1.25 }}>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            mb: 1,
          }}
        >
          Allowed by this structure type
        </Typography>

        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 }}>
          {LISTED_CAPABILITIES.map((field) => {
            const allowed = propertyTypes.includes(field.key);
            return (
              <Box
                component="li"
                key={field.key}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {allowed ? (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: 16, color: 'v4.accentDark', flexShrink: 0 }}
                  />
                ) : (
                  <RemoveCircleOutlineIcon
                    sx={{ fontSize: 16, color: 'v4.borderDark', flexShrink: 0 }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: '13px',
                    // `text.secondary` (5.74:1), not `text.disabled` (2.68:1):
                    // this is informational text, not an inactive control, so
                    // it has to clear AA while still reading as de-emphasised.
                    color: allowed ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {field.value}
                </Typography>
                {/* The tick/dash is the only thing separating the two states, so
                  spell the state out for a screen reader. */}
                <Box component="span" sx={visuallyHidden}>
                  {allowed ? 'allowed' : 'not allowed'}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    )}

    {(showGlobalReturns || showCentralSupplyWarehouse) && (
      <Box sx={{ borderTop: 1, borderColor: 'v4.border', mt: 1.25, pt: 1 }}>
        {showGlobalReturns && (
          <FormControlLabel
            control={
              <Checkbox
                checked={globalReturnsChecked}
                onChange={onGlobalReturnsChange}
                color={CheckboxColorProps.Primary}
                disabled={!canEditGlobalReturns}
              />
            }
            label={GLOBAL_RETURNS_LOCATION}
          />
        )}
        {showCentralSupplyWarehouse && onCentralSupplyWarehouseChange && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={centralSupplyWarehouseChecked}
                  onChange={onCentralSupplyWarehouseChange}
                  color={CheckboxColorProps.Primary}
                />
              }
              label={CENTRAL_SUPPLY_WAREHOUSE}
            />
            <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
              {CENTRAL_SUPPLY_WAREHOUSE_HELPER_TEXT}
            </FormHelperText>
          </Box>
        )}
      </Box>
    )}
  </Panel>
);
